/**
 * Общий модуль поиска и подсветки.
 *
 * Используется во всех трёх ветках: unifiedList (главный список), aux-selector и recursive-selector.
 * Единый алгоритм: токенизация → раскладочные варианты (RU/EN) → матчинг всех токенов → подсветка всех вхождений.
 */

let ruLayout: { toEn: (s: string) => string; fromEn: (s: string) => string } = {
  toEn: (s: string) => s,
  fromEn: (s: string) => s,
};

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  // @ts-ignore – convert-layout has no typings, require is provided by webpack / node
  ruLayout = require('convert-layout/ru');
} catch (_e) {
  // keep identity converters in non-browser/test environments
}

/** Inline-стиль для подсвеченных фрагментов (синий фон). */
export const HIGHLIGHT_STYLE = 'background: #D3E3FD; border-radius: 4px;';

/** Один фрагмент текста с опциональным стилем подсветки. */
export interface HighlightSpan {
  text: string;
  /** Пустая строка — обычный текст, непустая — подсвеченный фрагмент. */
  style: string;
}

/**
 * Убирает пустые и дубли из массива строк, каждую тримит.
 * Используется внутри makeVariants и tokenizeSearch.
 */
function uniqNonEmpty(values: string[]): string[] {
  return Array.from(new Set(values.map((v) => (v || '').trim()).filter(Boolean)));
}

function lower(value: string): string {
  return (value || '').toLowerCase();
}

/**
 * Генерирует раскладочные варианты строки: исходная (lowercase), переведённая в EN, переведённая из EN в RU.
 * Например, "Москва" → ["москва", "vjcrdf", ...].
 * Позволяет находить совпадения даже если пользователь набирает в неправильной раскладке.
 */
export function makeVariants(value: string): string[] {
  const v = value || '';
  return uniqNonEmpty([
    lower(v),
    lower(ruLayout.toEn(v)),
    lower(ruLayout.fromEn(v)),
  ]);
}

/**
 * Любой Unicode-знак препинания или символ. Используется для отбрасывания
 * вариантов токена, в которых появляются знаки препинания.
 *
 * Пример проблемы без фильтра: токен "аб" через toEn → "f," (а→f, б→`,`).
 * Этот вариант ловит ЛЮБУЮ "а" перед запятой в имени узла: "Москва," → "vjcrdf,"
 * содержит "f," → false-positive matched.
 *
 * По требованию: даже если user явно ввёл знаки препинания — варианты с ними
 * всё равно отбрасываются (включая исходный lowercase). Это сознательный отказ
 * от поиска по запросам со знаками препинания в обмен на отсутствие false-positive.
 */
const PUNCT_RE = /[\p{P}\p{S}]/u;

function hasPunctuation(value: string): boolean {
  return PUNCT_RE.test(value);
}

/**
 * Варианты для ТОКЕНА поиска. То же, что makeVariants, но варианты, содержащие
 * знаки препинания/символы, отбрасываются. Применяется к токенам пользовательского
 * ввода в applyAnalyticsSearchFilter и buildHighlightSpans, чтобы убрать
 * раскладочные false-positive вида "аб"→"f,".
 *
 * Для имён (name-кандидатов) используем makeVariants — там знаки препинания
 * в variantах не страшны: они НЕ ищутся, а лишь являются текстом-источником.
 */
export function makeTokenVariants(value: string): string[] {
  const v = value || '';
  return uniqNonEmpty([
    lower(v),
    lower(ruLayout.toEn(v)),
    lower(ruLayout.fromEn(v)),
  ]).filter((variant) => !hasPunctuation(variant));
}

/**
 * Разбивает строку поиска на отдельные токены по пробелам.
 * "мос тест" → ["мос", "тест"]. Пустые токены отбрасываются.
 */
export function tokenizeSearch(filter: string): string[] {
  return uniqNonEmpty((filter || '').split(/\s+/g));
}

/**
 * Проверяет, что ВСЕ поисковые токены найдены в тексте (логическое AND).
 *
 * @param normalizedCandidates — раскладочные варианты имени элемента (результат makeVariants(name)).
 * @param tokenVariantsCache — массив раскладочных вариантов каждого токена (tokens.map(t => makeVariants(t))).
 *
 * Для каждого токена достаточно, чтобы хотя бы один его вариант нашёлся как подстрока
 * хотя бы в одном кандидате (any-to-any). Но все токены должны совпасть (every).
 */
export function matchesTokens(normalizedCandidates: string[], tokenVariantsCache: string[][]): boolean {
  if (!tokenVariantsCache.length) return true;
  return tokenVariantsCache.every((tokenVariants) =>
    tokenVariants.some((variant) =>
      normalizedCandidates.some((candidate) => candidate.includes(variant)),
    ),
  );
}

/**
 * Сливает пересекающиеся/смежные диапазоны [start, end) в один.
 * [{0,3}, {2,5}] → [{0,5}]. Нужно чтобы подсветка не дублировала символы при пересечении токенов.
 */
function mergeRanges(ranges: Array<{ start: number; end: number }>) {
  if (!ranges.length) return ranges;

  const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Array<{ start: number; end: number }> = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
}

/**
 * Строит массив HighlightSpan для отображения текста с подсветкой найденных вхождений.
 *
 * Алгоритм:
 * 1. Для каждого токена генерируются раскладочные варианты БЕЗ знаков препинания (makeTokenVariants).
 * 2. Для source генерируются ВСЕ раскладочные варианты (makeVariants): lower + toEn + fromEn.
 *    convert-layout/ru заменяет символы 1:1 (длина и позиции совпадают), поэтому индекс совпадения
 *    в любом variantе соответствует позиции в оригинальном тексте. Это позволяет подсвечивать
 *    как русские, так и нерусские (раскладочные) вхождения в одном имени.
 * 3. Все собранные диапазоны сливаются (mergeRanges).
 * 4. Текст нарезается на чередующиеся фрагменты: обычный текст (style='') и подсвеченный (style=HIGHLIGHT_STYLE).
 *
 * @param text — исходный текст (имя элемента).
 * @param tokens — массив поисковых токенов (результат tokenizeSearch).
 * @param style — CSS inline-стиль для подсвеченных фрагментов (по умолчанию HIGHLIGHT_STYLE).
 * @returns Массив фрагментов для рендера через v-for + :style.
 */
export function buildHighlightSpans(text: string, tokens: string[], style = HIGHLIGHT_STYLE): HighlightSpan[] {
  const source = text || '';
  if (!source.length || !tokens.length) return [{ text: source, style: '' }];

  const sourceVariants = makeVariants(source);
  const tokenVariants = uniqNonEmpty(tokens.flatMap((t) => makeTokenVariants(t)));
  if (!tokenVariants.length) return [{ text: source, style: '' }];

  const ranges: Array<{ start: number; end: number }> = [];

  sourceVariants.forEach((sourceVariant) => {
    tokenVariants.forEach((token) => {
      let cursor = 0;
      while (cursor <= sourceVariant.length - token.length) {
        const idx = sourceVariant.indexOf(token, cursor);
        if (idx === -1) break;
        ranges.push({ start: idx, end: idx + token.length });
        cursor = idx + 1;
      }
    });
  });

  if (!ranges.length) return [{ text: source, style: '' }];

  const merged = mergeRanges(ranges);
  const spans: HighlightSpan[] = [];
  let cursor = 0;

  merged.forEach((range) => {
    if (range.start > cursor) spans.push({ text: source.slice(cursor, range.start), style: '' });
    spans.push({ text: source.slice(range.start, range.end), style });
    cursor = range.end;
  });

  if (cursor < source.length) spans.push({ text: source.slice(cursor), style: '' });
  return spans;
}
